import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from 'react-toastify';
import { useIsPresent } from 'framer-motion';
import './AppToastProvider.css';

const ToastSlotContext = createContext(null);

// Keep one ToastContainer alive across login redirects. Only its owned host
// moves; toasts, timers, close handlers and the library's alert roles survive.
export default function AppToastProvider({ children }) {
  const [host] = useState(() => {
    if (typeof document === 'undefined') return null;
    const node = document.createElement('div');
    node.className = 'speakable-toast-host';
    return node;
  });
  const fallbackRef = useRef(null);
  const slotsRef = useRef(new Map());

  const moveHost = useCallback((target, docked) => {
    if (!host || !target) return;
    const focused = document.activeElement;
    const restoreFocus = host.contains(focused);
    host.classList.toggle('speakable-toast-host--docked', docked);
    if (host.parentNode !== target) target.appendChild(host);
    if (restoreFocus && focused.isConnected && document.activeElement !== focused) {
      focused.focus({ preventScroll: true });
    }
  }, [host]);

  const syncHost = useCallback(() => {
    let target = null;
    let priority = -1;
    // Navigation scopes temporarily outrank routes. Equal-priority scopes use
    // registration order, so closing the newest restores the surviving owner.
    for (const [node, rank] of slotsRef.current) {
      if (node.isConnected && rank >= priority) {
        target = node;
        priority = rank;
      }
    }
    moveHost(target || fallbackRef.current, Boolean(target));
  }, [moveHost]);

  const registerSlot = useCallback((node, previous, scope) => {
    if (previous) slotsRef.current.delete(previous);
    if (node) slotsRef.current.set(node, scope === 'navigation' ? 1 : 0);
    // Ref detachment precedes DOM removal: move the live host synchronously,
    // without remounting its container or discarding a still-mounted route.
    syncHost();
  }, [syncHost]);

  const attachFallback = useCallback((node) => {
    fallbackRef.current = node;
    if (node) syncHost();
  }, [syncHost]);

  useLayoutEffect(() => {
    // StrictMode replays layout effects without recreating the host or refs.
    syncHost();
    return () => { host?.remove(); };
  }, [host, syncHost]);

  return (
    <ToastSlotContext.Provider value={registerSlot}>
      {children}
      <div ref={attachFallback} data-app-toast-fallback="" />
      {host && createPortal(
        <ToastContainer
          className="speakable-toast-container"
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          limit={1}
          style={{ zIndex: 9999, position: 'fixed' }}
        />,
        host,
      )}
    </ToastSlotContext.Provider>
  );
}

// Route slots reserve ordinary flow space. A mounted navigation slot owns the
// same host until it closes, then the prior live route (or fallback) resumes.
export function AppToastSlot({ name, scope = 'route' }) {
  // AnimatePresence retains exiting DOM. Release its ownership at close, not
  // after its fade/slide removes the panel, without changing that animation.
  const present = useIsPresent();
  const registerSlot = useContext(ToastSlotContext);
  const nodeRef = useRef(null);
  const attach = useCallback((node) => {
    const previous = nodeRef.current;
    nodeRef.current = node;
    registerSlot?.(node, previous, scope);
  }, [registerSlot, scope]);

  return <div ref={present ? attach : null} className="speakable-toast-slot" data-app-toast-slot={name} data-app-toast-scope={scope} />;
}
